<template>
  <div v-if="$can('view', 'Yacht')">
    <div class="subtitle-1 pa-4">
      <v-row
        no-gutters
        align="center"
      >
        <v-col>
          Main Details
        </v-col>
      </v-row>
    </div>
    <v-card class="elevation-10 radius-10 mb-12">
      <v-card-text class="px-6 pb-6">
        <!--<editor-fold description="Basic Yacht Specs">-->
        <div>
          <yacht-data-row
            label="Hull Number"
            striped
          >
            <span v-if="yacht.hull_number">
              {{ yacht.hull_number }}
            </span>
            <a
              v-else-if="$can('edit-basic-details', 'Yacht')"
              href="#"
              @click.prevent="$emit('edit:yacht-basic-details')"
            >
              Add hull number
            </a>
            <span
              v-else
              class="grey--text"
            >
              None
            </span>
          </yacht-data-row>
          <yacht-data-row
            label="Engine(s)"
            striped
          >
            {{ yacht.model.engines_description }}
          </yacht-data-row>
          <yacht-data-row
            label="Engine Serial Numbers"
            striped
          >
            <v-row
              v-if="yacht.engine_serial_numbers && yacht.engine_serial_numbers.length > 0"
              no-gutters
            >
              <v-col>
                <div
                  v-for="(engine, index) in yacht.engine_serial_numbers"
                  :key="'engine_serial_number_' + index"
                >
                  {{ engine.serial_number || 'not specified' }}
                </div>
              </v-col>
              <v-col cols="auto">
                <a
                  v-if="$can('edit-basic-details', 'Yacht')"
                  href="#"
                  @click="$emit('edit:serial-numbers')"
                >
                  Edit
                </a>
              </v-col>
            </v-row>
            <div v-else>
              <a
                v-if="$can('edit-basic-details', 'Yacht')"
                href="#"
                @click.prevent="$emit('edit:serial-numbers')"
              >
                Add engine serial number(s)
              </a>
            </div>
          </yacht-data-row>
          <yacht-data-row
            label="Cabins"
            striped
          >
            {{ yacht.model.cabin_count }} guest cabins, {{ yacht.model.crew_cabin_count }} crew cabins
          </yacht-data-row>
          <yacht-data-row
            label="Berths"
            striped
          >
            {{ yacht.model.berth_count }} RNOG, {{ yacht.model.crew_berth_count }} crew berths
          </yacht-data-row>
          <yacht-data-row
            label="Length (LOA)"
            striped
          >
            {{ yacht.model.length | fromMetric('m', 2) }}
          </yacht-data-row>
          <yacht-data-row
            label="Beam"
            striped
          >
            {{ yacht.model.beam | fromMetric('m', 2) }}
          </yacht-data-row>
          <yacht-data-row
            label="Draught"
            striped
          >
            {{ yacht.model.draught | fromMetric('m', 2) }}
          </yacht-data-row>
          <yacht-data-row
            label="Mast height"
            striped
          >
            {{ yacht.model.mast_height | fromMetric('m', 2) }}
          </yacht-data-row>
          <yacht-data-row
            label="Sail area main & furling genoa"
            striped
          >
            {{ yacht.model.main_sail_area | fromMetric('m2', 2) }}
          </yacht-data-row>
          <yacht-data-row
            label="Light displacement"
            striped
          >
            {{ yacht.model.displacement | fromMetric('kg', 2) }}
          </yacht-data-row>
          <yacht-data-row
            label="Toilletes"
            striped
          >
            {{ yacht.model.toilette_count }}
          </yacht-data-row>
          <yacht-data-row
            label="Steering wheels"
            striped
          >
            {{ yacht.model.steering_wheel_count }}
          </yacht-data-row>
          <yacht-data-row
            label="Fuel tank(s)"
            striped
          >
            {{ yacht.model.fuel_tanks_description }} l
          </yacht-data-row>
          <yacht-data-row
            label="Water tank(s)"
            striped
          >
            {{ yacht.model.water_tanks_description }} l
          </yacht-data-row>
        </div>
        <!--</editor-fold>-->
      </v-card-text>
    </v-card>

    <div class="subtitle-1 pa-4">
      <v-row
        no-gutters
        align="center"
      >
        <v-col>
          Equipment / Specs Overview
        </v-col>
        <v-col
          v-if="$can('update-specs', 'Yacht')"
          cols="auto"
        >
          <v-btn
            text
            color="grey"
            rounded
            small
            class="ma-0"
            @click="yachtSpecificationsEditDialogVisible = true"
          >
            <v-icon
              left
              small
            >
              edit
            </v-icon>
            Edit
          </v-btn>
        </v-col>
      </v-row>
    </div>
    <v-card class="elevation-10 mb-12">
      <v-card-text class="px-6 pb-6">
        <!--<editor-fold description="Basic Yacht Specs">-->
        <div>
          <v-row no-gutters>
            <v-col
              v-for="spec in yacht.specifications"
              :key="spec.key"
              cols="8"
              class="grid--striped"
            >
              <yacht-data-row
                :label="spec.title"
                class="px-3"
              >
                <span :class="{ 'grey--text': spec.value === null }">
                  {{ spec.value === true ? 'Yes' : (spec.value === false ? 'No' : (Array.isArray(spec.value) ? spec.value.join(', ') : (spec.value || 'unknown'))) }}
                </span>
              </yacht-data-row>
            </v-col>
            <v-col
              v-for="(spec, index) in yacht.custom_specifications"
              :key="'custom_spec_'+index"
              cols="8"
              class="grid--striped"
            >
              <yacht-data-row
                :label="spec.title"
                class="px-3"
              >
                <span :class="{ 'grey--text': spec.value === null }">
                  {{ spec.value }}
                </span>
              </yacht-data-row>
            </v-col>
          </v-row>
        </div>
        <!--</editor-fold>-->
      </v-card-text>
    </v-card>

    <div class="subtitle-1 pa-4">
      <v-row
        no-gutters
        align="center"
      >
        <v-col>
          Equipment Packs
        </v-col>
      </v-row>
    </div>
    <v-card class="elevation-10 mb-12">
      <v-card-text class="px-6 pb-6">
        <!--<editor-fold description="Basic Yacht Specs">-->
        <div>
          <v-simple-table>
            <thead class="v-data-table-header">
              <tr>
                <th class="text-left">
                  Label
                </th>
                <th
                  v-if="$can('view-sale-price', 'YachtEquipmentPack')"
                  class="text-left"
                >
                  Price
                </th>
                <th>
                  Installed by
                </th>
                <th class="text-left">
                  Supplier
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="pack in yacht.equipment_packs"
                :key="pack.id"
                @click="viewPackItems(pack.id)"
              >
                <td
                  style="max-width: 150px"
                  class="text-truncate"
                >
                  <div v-if="pack.category">
                    <v-icon
                      style="font-size: 10px"
                      :color="pack.category.color"
                      class="mr-2"
                    >
                      circle
                    </v-icon>
                    {{ pack.category.name }}
                  </div>
                </td>
                <td class="font-weight-bold">
                  {{ pack.label }}
                </td>
                <td
                  v-if="$can('view-sale-price', 'YachtEquipmentPack')"
                  class="text-no-wrap"
                >
                  {{ pack.pivot_sale_price | currency(pack.currency) }}
                </td>
                <td>
                  {{ installedByData[pack.installed_by].label }}
                </td>
                <td>
                  <span v-if="pack.supplier">
                    {{ pack.supplier.name }}
                  </span>
                  <span
                    v-else
                    class="grey--text"
                  >
                    N/A
                  </span>
                </td>
                <td>
                  <v-btn
                    color="primary"
                    text
                    small
                    rounded
                    class="text-none"
                    @click="viewPackItems(pack.id)"
                  >
                    <v-icon
                      left
                      small
                    >
                      fa fa-list
                    </v-icon>
                    Items
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </div>
      </v-card-text>
    </v-card>

    <yacht-specifications-edit-dialog
      :specifications="yacht.specifications"
      :visible.sync="yachtSpecificationsEditDialogVisible"
      :yacht.sync="yachtDirty"
    />

    <yacht-equipment-items-dialog
      v-if="currentPack"
      :pack="currentPack"
      :visible.sync="packItemsDialogVisible"
    />
  </div>
</template>

<script>
import YachtDataRow from '@/components/business/yachts/details/YachtDataRow'
import YachtSpecificationsEditDialog from '@/components/business/yachts/YachtSpecificationsEditDialog'
import YachtEquipmentItemsDialog from '@/components/business/yachts/YachtEquipmentItemsDialog'
import YachtEquipmentPack from '@/models/YachtEquipmentPack'

export default {
  name: 'Specs',
  components: { YachtEquipmentItemsDialog, YachtSpecificationsEditDialog, YachtDataRow },
  props: {
    yacht: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      yachtSpecificationsEditDialogVisible: false,
      packItemsDialogVisible: false,
      currentPackId: null
    }
  },
  computed: {
    yachtDirty: {
      set: function (value) {
        this.$emit('update:yacht', value)
      },
      get: function () {
        return this.yacht
      }
    },
    currentPack: function () {
      if (!this.currentPackId) return undefined

      return this.yacht.equipment_packs.find(p => p.id === this.currentPackId)
    }
  },
  created() {
    this.installedByData = YachtEquipmentPack.installedByData
  },
  methods: {
    viewPackItems(packId) {
      this.currentPackId = packId

      this.packItemsDialogVisible = true
    }
  }
}
</script>
